import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../../../shared/context/clinica-context";
import "../List.scss";

import { abreviarNome } from "../../../../../../shared/util/helperFunctions";
import { podeAlterarRegisto } from "../../../../../../shared/util/unidadeRegisto";
import SeloSoConsulta from "../../../../../../shared/components/badgeUnidade/SeloSoConsulta";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import { Trash2, Key, Edit, FileText, Eye, DollarSign } from "lucide-react";
import { format } from "date-fns";

import { Popover } from "antd";
import { Popconfirm } from "antd";
import BadgeUnidade from "../../../../../../shared/components/badgeUnidade/BadgeUnidade";

function ListAvaliacao(props) {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const navigate = useNavigate();

  const getMenuContent = (row) => {
    const menuItems = [];
    let hasContent = false;

    /* Numa avaliação de outra unidade separa-se o que se pode fazer:
     *
     *   NÃO — editar, desativar, fechar e abrir. São alterações à avaliação
     *   em si, que é da outra unidade.
     *
     *   SIM — lançar débito. Isso cria dinheiro a receber, e o dinheiro é de
     *   quem o cobra. Fica com aviso, para não passar despercebido.
     *
     * (Gerar orçamento também é permitido, mas vive no ícone da linha e não
     * neste menu.) */
    const daMinhaUnidade = podeAlterarRegisto(row.estabelecimento, auth);
    const avisoOutraUnidade = `Esta avaliação é de ${
      row.estabelecimento?.nome || "outra unidade"
    }. O débito será lançado nesta unidade.`;

    const eResponsavel =
      ((auth.role === "Médico/Administrador" || auth.role === "Dentista") &&
        row.medico_responsavel._id === auth.userId) ||
      ((auth.role === "Secretária(o)" || auth.role === "Assistente") &&
        auth.usuariosAssociados.some(
          (medico) => medico.id === row.medico_responsavel.id
        ));

    if (daMinhaUnidade && eResponsavel) {
      if (
        !row.terminado &&
        !row.transformado &&
        row.status !== "INTERROMPIDO"
      ) {
        // Condition 1: Edit and Delete options
        hasContent = true;

        if (auth.perm.includes("u-aval")) {
          menuItems.push(
            <div
              key="edit"
              className="popOverMenu--option"
              onClick={() => navigate(`/avaliacao/edit/${row.id}`)}
            >
              <span>Editar</span>
              <Edit className="popOverMenu--option__icon" />
            </div>
          );
        }

        if (auth.perm.includes("d-aval")) {
          menuItems.push(
            <Popconfirm
              key="deactivate"
              title="Desativação de avaliação"
              description="Tem a certeza que pretende desativar a avaliação?"
              icon={<Trash2 style={{ color: "red" }} />}
              okText="Sim"
              cancelText="Não"
              onConfirm={() => props.handleDesativar(row.id)}
            >
              <div className="popOverMenu--option">
                <span>Desativar</span>
                <Trash2 className="popOverMenu--option__icon" />
              </div>
            </Popconfirm>
          );
        }
      }

      // Condition 2: Close option
      if (
        row.transformado &&
        !row.terminado &&
        !row.fechado &&
        row.status !== "INTERROMPIDO" &&
        auth.perm.includes("abrFech-aval")
      ) {
        hasContent = true;
        menuItems.push(
          <Popconfirm
            key="close"
            title="Fecho de avaliação"
            description="Tem a certeza que pretende fechar a avaliação?"
            icon={<Key style={{ color: "red" }} />}
            okText="Sim"
            cancelText="Não"
            onConfirm={() => props.handleFecharAbrir(row.id)}
          >
            <div className="popOverMenu--option">
              <span>Fechar</span>
              <Key className="popOverMenu--option__icon" />
            </div>
          </Popconfirm>
        );
      }

      // Condition 3: Open option
      if (
        row.fechado &&
        row.status !== "INTERROMPIDO" &&
        auth.perm.includes("abrFech-aval")
      ) {
        hasContent = true;
        menuItems.push(
          <Popconfirm
            key="open"
            title="Abertura de avaliação"
            description="Tem a certeza que pretende abrir a avaliação?"
            icon={<Key style={{ color: "red" }} />}
            okText="Sim"
            cancelText="Não"
            onConfirm={() => props.handleFecharAbrir(row.id)}
          >
            <div className="popOverMenu--option">
              <span>Abrir</span>
              <Key className="popOverMenu--option__icon" />
            </div>
          </Popconfirm>
        );
      }

    }

    /* Lançar débito FORA do bloco da unidade, de propósito.
     *
     * Lançar o débito de uma avaliação é cobrar — e cobrar é dinheiro, que é de
     * quem o recebe. Uma avaliação feita na Praia pode ser cobrada em Assomada
     * se for lá que o paciente aparece. O aviso vai na confirmação, para a
     * pessoa saber onde o débito vai ficar. */
    if (
      eResponsavel &&
      !row.fechado &&
      row.status !== "INTERROMPIDO" &&
      !row.debitado &&
      clinica.clinica.configuracoes.configuracoes.avaliacao.ativo &&
      clinica.clinica.configuracoes.configuracoes.avaliacao.preco > 0 &&
      auth.perm.includes("deb-aval")
    ) {
      hasContent = true;
      menuItems.push(
        <Popconfirm
          key="debit"
          title="Lançar o valor da Avaliação em débito?"
          description={
            daMinhaUnidade
              ? "Tem a certeza que pretende lançar o valor da avaliação em débito?"
              : avisoOutraUnidade +
                " Tem a certeza que pretende lançar o valor da avaliação em débito?"
          }
          icon={<DollarSign style={{ color: "red" }} />}
          okText="Sim"
          cancelText="Não"
          onConfirm={() => props.handleLancarDebito(row.id)}
        >
          <div className="popOverMenu--option">
            <span>Lançar débito</span>
            <DollarSign className="popOverMenu--option__icon" />
          </div>
        </Popconfirm>
      );
    }

    return hasContent ? (
      <div className="dots__menu__popup">{menuItems}</div>
    ) : (
      "vazio"
    );
  };

  return (
    <TableContainer component={Paper} className="table">
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead style={{ background: "#fff" }}>
          <TableRow>
            <TableCell className="tableCell tableCell--heading tableCell--heading--start">
              Código
            </TableCell>
            <TableCell className="tableCell tableCell--heading tableCell--heading--start">
              Criado por
            </TableCell>
            <TableCell className="tableCell tableCell--heading tableCell--heading--start">
              Médico Responsável
            </TableCell>
            <TableCell className="tableCell tableCell--heading">
              Data Avaliação
            </TableCell>
            <TableCell className="tableCell tableCell--heading">
              Estado
            </TableCell>
            <TableCell className="tableCell tableCell--heading tableCell--heading--end">
              Ações
            </TableCell>
          </TableRow>
        </TableHead>

        {props.data && (
          <TableBody>
            {props.data.map((row) => {
              const menuContent = getMenuContent(row);

              return (
                <TableRow key={row.id}>
                  <TableCell className="tableCell">
                    {"AV" +
                      row.numero +
                      "_" +
                      format(new Date(row.created_at), "yyyy")}
                    <BadgeUnidade estabelecimento={row.estabelecimento} />
                    <SeloSoConsulta estabelecimento={row.estabelecimento} />
                  </TableCell>
                  <TableCell className="tableCell">
                    {abreviarNome(row.criado_por?.name)}
                  </TableCell>
                  <TableCell className="tableCell">
                    {abreviarNome(row.medico_responsavel.name)}
                  </TableCell>
                  <TableCell className="tableCell">
                    {row.data_avaliacao}
                  </TableCell>
                  <TableCell className="tableCell">
                    <span
                      className={`status ${
                        row.status
                          ? row.status
                          : row.terminado && !row.fechado
                          ? "concluido"
                          : row.transformado && !row.fechado
                          ? "transformado"
                          : !row.terminado && !row.transformado && !row.fechado
                          ? "pendente"
                          : row.fechado
                          ? "fechado"
                          : ""
                      }`}
                    >
                      {row.status === "INTERROMPIDO"
                        ? "Interrompido"
                        : row.terminado && !row.fechado
                        ? "Concluido"
                        : row.transformado && !row.fechado
                        ? "Transformado"
                        : !row.transformado && !row.terminado && !row.fechado
                        ? "Criado"
                        : row.fechado
                        ? "Fechado"
                        : ""}
                    </span>
                  </TableCell>
                  <TableCell className="tableCell">
                    <div className="tableCell__action">
                      {auth.perm.includes("c-orc") &&
                        !row.terminado &&
                        !row.fechado &&
                        (((auth.role === "Médico/Administrador" ||
                          auth.role === "Dentista") &&
                          row.medico_responsavel._id === auth.userId) ||
                          ((auth.role === "Secretária(o)" ||
                            auth.role === "Assistente") &&
                            auth.usuariosAssociados.some(
                              (medico) =>
                                medico.id === row.medico_responsavel.id
                            ))) && (
                          <Tooltip
                            title={
                              podeAlterarRegisto(row.estabelecimento, auth)
                                ? "Gerar Orçamento"
                                : `Esta avaliação é de ${
                                    row.estabelecimento?.nome || "outra unidade"
                                  }. O orçamento será criado nesta unidade, com os preços daqui.`
                            }
                          >
                            {/* Orçamentar uma avaliação da outra unidade é
                                permitido de propósito: o orçamento nasce AQUI,
                                com os preços daqui. É o caminho que a dona
                                indicou para quando os preços divergirem — em
                                vez de pagar o orçamento de lá, faz-se um novo
                                aqui. O aviso está no tooltip. */}
                            <FileText
                              className="icon__list"
                              onClick={() =>
                                navigate(`/orcamentos/new/${row.id}`)
                              }
                            />
                          </Tooltip>
                        )}
                      {(!auth.perm.includes("c-orc") || row.terminado) && (
                        <div>&#12644;&#12644;&#12644;</div>
                      )}
                      {auth.perm.includes("r-aval") && (
                        <Tooltip title="Ver Avaliação">
                          <Eye
                            className="icon__list"
                            onClick={() =>
                              props.handleOpenModalAvaliacao(row.id)
                            }
                          />
                        </Tooltip>
                      )}

                      {row.debitado && (
                        <Tooltip
                          title={
                            row.entrada?.entradaRecebida
                              ? "Avaliação Recebida"
                              : "Débito Lançado"
                          }
                        >
                          <DollarSign
                            className="icon__list"
                            style={{
                              color: row.entrada?.entradaRecebida
                                ? "12d453"
                                : "#fdcb7f",
                              cursor: "default",
                            }}
                          />
                        </Tooltip>
                      )}
                      {!auth.perm.includes("r-aval") && (
                        <div>&#12644;&#12644;&#12644;</div>
                      )}

                      {menuContent !== "vazio" && (
                        <Popover
                          content={menuContent}
                          trigger="click"
                          placement="bottom"
                        >
                          <div className="dots__menu">
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                          </div>
                        </Popover>
                      )}
                      {menuContent === "vazio" && (
                        <div>&#12644;&#12644;&#12644;</div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
}

export default ListAvaliacao;
